import { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import PlanSelectionModal from "./PlanSelectionModal";

interface OpenOptions {
  defaultPlan?: string;
}

interface SubscribeModalContextValue {
  /** Ouvre le modal de choix de plan. En abonnement actif → mode changement. */
  openSubscribe: (opts?: OpenOptions) => void;
}

const SubscribeModalContext = createContext<SubscribeModalContextValue>({
  openSubscribe: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useSubscribeModal = () => useContext(SubscribeModalContext);

/**
 * Point d'entrée UNIQUE pour s'abonner / changer de plan. Toutes les entrées
 * (bannière d'essai, sidebar, carte abonnement) ouvrent ce même modal via
 * openSubscribe(), pour un parcours cohérent.
 */
export function SubscribeModalProvider({ children }: { children: ReactNode }) {
  const { subscription, refreshSubscription } = useAuth();
  const [open, setOpen] = useState(false);
  const [defaultPlan, setDefaultPlan] = useState<string | undefined>(undefined);

  const openSubscribe = useCallback((opts?: OpenOptions) => {
    setDefaultPlan(opts?.defaultPlan);
    setOpen(true);
  }, []);

  const handlePlanChanged = useCallback(() => {
    let attempts = 0;
    const tryRefresh = async () => {
      await refreshSubscription();
      attempts++;
      if (attempts < 5) setTimeout(tryRefresh, 2000);
    };
    tryRefresh();
  }, [refreshSubscription]);

  return (
    <SubscribeModalContext.Provider value={{ openSubscribe }}>
      {children}
      <PlanSelectionModal
        open={open}
        defaultPlan={defaultPlan}
        onClose={() => setOpen(false)}
        currentProductId={subscription.subscribed ? subscription.product_id : null}
        onPlanChanged={handlePlanChanged}
      />
    </SubscribeModalContext.Provider>
  );
}
